import { MODULE_NAME } from './const';
import {
	userSpecificConfiguration as userSpecificConfigurationGetter,
	generalConfiguration as generalConfigurationGetter,
} from './modules/configurations/getters';
import StoreModuleService from '../StoreModuleService';
import { getNodeByUuidForWorkflowVersion, useIsRootNode } from '../nodes/getters';
import { isNarrowBranchesLayoutEnabled as isNarrowBranchesLayoutFeatureFlagEnabled } from '@/store/modules/featureFlags/getters';
import {
	doesWorkflowHasInstallable,
	getWorkflow,
	getWorkflowDraftNodesDataByUUID,
	getWorkflowDraftVersion,
	getWorkflowInstallable,

	// getters from `workflows` that are being "shadowed" here in `selectedWorkflow`
	nodesData as wfNodesData,
	nodesDataArray as wfNodesDataArray,
	nodesDataSorted as wfNodesDataSorted,
	rootNode as wfRootNode,
	element as wfElement,
	uiElement as wfUiElement,
	nativeElement as wfNativeElement,
	isNativeElement as wfIsNativeElement,
	elementContextualData as wfElementContextualData,
	workflowSharedData,
	isWorkflowSharedWithOthers,
} from '@/store/modules/workflows/getters';
import AppActionService from '@/utils/AppActionService';
import WorkflowService from '@/utils/WorkflowService';
import WorkflowConfigurationService from '@/utils/workflow-configuration-service';
import * as WorkflowNodeService from '@/utils/workflow-node-service';

const buildSelectedWorkflowGetterObject = (getter) => StoreModuleService.buildGetterObject(MODULE_NAME, getter);

// getters from `workflows` that are being "shadowed" here in `selectedWorkflow`
export const nodesData = buildSelectedWorkflowGetterObject('nodesData');
export const nodesDataArray = buildSelectedWorkflowGetterObject('nodesDataArray');
export const nodesDataSorted = buildSelectedWorkflowGetterObject('nodesDataSorted');
export const rootNode = buildSelectedWorkflowGetterObject('rootNode');
export const element = buildSelectedWorkflowGetterObject('element');
export const uiElement = buildSelectedWorkflowGetterObject('uiElement');
export const nativeElement = buildSelectedWorkflowGetterObject('nativeElement');
export const isNativeElement = buildSelectedWorkflowGetterObject('isNativeElement');
export const elementContextualData = buildSelectedWorkflowGetterObject('elementContextualData');

// </these shadow getters from workflows store module>

export const getWorkflowAppearanceSettings = buildSelectedWorkflowGetterObject('getWorkflowAppearanceSettings');
export const customerAuthSettings = buildSelectedWorkflowGetterObject('customerAuthSettings');

export const contextualLoading = buildSelectedWorkflowGetterObject('contextualLoading');
export const selectedWorkflowId = buildSelectedWorkflowGetterObject('selectedWorkflowId');
export const editWorkflow = buildSelectedWorkflowGetterObject('editWorkflow');
export const editedWorkflowVersion = buildSelectedWorkflowGetterObject('editedWorkflowVersion');
export const nodesDataByUUID = buildSelectedWorkflowGetterObject('nodesDataByUUID');
export const getNextNodeByNodeId = buildSelectedWorkflowGetterObject('getNextNodeByNodeId');
export const actionContextNode = buildSelectedWorkflowGetterObject('actionContextNode');
export const asyncEnabled = buildSelectedWorkflowGetterObject('asyncEnabled');

export const isOfTypeSubFlow = buildSelectedWorkflowGetterObject('isOfTypeSubFlow');
export const isOfTypeTool = buildSelectedWorkflowGetterObject('isOfTypeTool');
export const isOfTypeFeature = buildSelectedWorkflowGetterObject('isOfTypeFeature');
export const isOfTypeCloud = buildSelectedWorkflowGetterObject('isOfTypeCloud');
export const isOfTypeScheduledCloud = buildSelectedWorkflowGetterObject('isOfTypeScheduledCloud');
export const isOfTypeSlack = buildSelectedWorkflowGetterObject('isOfTypeSlack');
export const isPortal = buildSelectedWorkflowGetterObject('isPortal');

export const isSharedUsersAccess = buildSelectedWorkflowGetterObject('isSharedUsersAccess');

export const triggerUrl = buildSelectedWorkflowGetterObject('triggerUrl');
export const isActionAllowedForSelectedWorkflow = buildSelectedWorkflowGetterObject('isActionAllowedForSelectedWorkflow');
export const isInterfacePreviewEnabled = buildSelectedWorkflowGetterObject('isInterfacePreviewEnabled');

export const generalSetupMappedFields = buildSelectedWorkflowGetterObject('generalSetupMappedFields');
export const userSetupMappedFields = buildSelectedWorkflowGetterObject('userSetupMappedFields');
export const replaceInsertDataStringDictionary = buildSelectedWorkflowGetterObject('replaceInsertDataStringDictionary');

export const getSelectedWorkflowInstallable = buildSelectedWorkflowGetterObject('getSelectedWorkflowInstallable');
export const editWorkflowHasInstallable = buildSelectedWorkflowGetterObject('editWorkflowHasInstallable');

export const isPublishLoading = buildSelectedWorkflowGetterObject('isPublishLoading');

export const processWorkflowIds = buildSelectedWorkflowGetterObject('processWorkflowIds');
export const processWorkflows = buildSelectedWorkflowGetterObject('processWorkflows');
export const getNodeByUuidForSelectedWorkflowVersion = buildSelectedWorkflowGetterObject('getNodeByUuidForSelectedWorkflowVersion');
export const getNodeTestResultByUuidForSelectedWorkflowVersion = buildSelectedWorkflowGetterObject(
	'getNodeTestResultByUuidForSelectedWorkflowVersion'
);
export const sharedData = buildSelectedWorkflowGetterObject('sharedData');
export const isSharedWithOthers = buildSelectedWorkflowGetterObject('isSharedWithOthers');
export const subflowNodes = buildSelectedWorkflowGetterObject('subflowNodes');
export const isRootNodeOfSubFlow = buildSelectedWorkflowGetterObject('isRootNodeOfSubFlow');
export const isNarrowBranchesLayout = buildSelectedWorkflowGetterObject('isNarrowBranchesLayout');

const getters = {
	[replaceInsertDataStringDictionary.getter]: (state, getters, rootState, rootGetters) => {
		try {
			const userSpecificConfiguration = rootGetters[userSpecificConfigurationGetter.fullPath];
			const generalConfiguration = rootGetters[generalConfigurationGetter.fullPath];
			return WorkflowConfigurationService.getReplaceInsertDataStringDictionary(generalConfiguration, userSpecificConfiguration);
		} catch (err) {
			console.error(err);
			return {};
		}
	},
	[getNodeByUuidForSelectedWorkflowVersion.getter]: (state, getters, rootState, rootGetters) => (nodeUUID) => {
		const wfv = getters[editedWorkflowVersion.getter]?.id;
		return rootGetters[getNodeByUuidForWorkflowVersion.fullPath](nodeUUID, wfv);
	},
	[getNodeTestResultByUuidForSelectedWorkflowVersion.getter]:
		(state, getters) =>
		(nodeUUID, cleanHiddenFields = true) => {
			const wfv = getters[editedWorkflowVersion.getter]?.id;

			const nodeData = getters[getNodeByUuidForSelectedWorkflowVersion.getter](nodeUUID, wfv);
			return WorkflowNodeService.getNodeTestResult(nodeData, cleanHiddenFields);
		},
	[contextualLoading.getter]: (state) => state.contextualLoading,
	[selectedWorkflowId.getter]: (state) => state.selectedWorkflowId,
	[triggerUrl.getter]: (state) => state.triggerUrl,
	[editWorkflow.getter]: (state, getters, rootState, rootGetters) => rootGetters[getWorkflow.fullPath](state.selectedWorkflowId),
	[editedWorkflowVersion.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[getWorkflowDraftVersion.fullPath](state.selectedWorkflowId),
	[nodesDataByUUID.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[getWorkflowDraftNodesDataByUUID.fullPath]({ workflowId: state.selectedWorkflowId }),
	[actionContextNode.getter]: (state, getters) => getters[nodesData.getter][getters[element.getter]?.action_context_node],
	[asyncEnabled.getter]: (state, getters) => getters[editedWorkflowVersion.getter].async_enabled,
	[isOfTypeSubFlow.getter]: (state, getters) => WorkflowService.isOfTypeSubFlow(getters[editWorkflow.getter]),
	[isOfTypeTool.getter]: (state, getters) => WorkflowService.isOfTypeTool(getters[editWorkflow.getter]),
	[isOfTypeFeature.getter]: (state, getters) => WorkflowService.isOfTypeFeature(getters[editWorkflow.getter]),
	[isOfTypeCloud.getter]: (state, getters) => WorkflowService.isOfTypeCloud(getters[editWorkflow.getter]),
	[isOfTypeScheduledCloud.getter]: (state, getters) => WorkflowService.isOfTypeScheduledCloud(getters[editWorkflow.getter]),
	[isOfTypeSlack.getter]: (state, getters) => WorkflowService.isOfTypeSlack(getters[editWorkflow.getter]),
	[isPortal.getter]: (state, getters) =>
		WorkflowService.isOfTypeTool(getters[editWorkflow.getter]) && WorkflowService.isPublicAccess(getters[editWorkflow.getter]),
	[isSharedUsersAccess.getter]: (state, getters) => WorkflowService.isSharedUsersAccess(getters[editWorkflow.getter]),
	[isActionAllowedForSelectedWorkflow.getter]: (state, getters) => (action) => {
		const isNotification = getters[editWorkflow.getter].is_notification;
		const isAllowed = AppActionService.isAllowedForElementContext(action, getters[element.getter]);
		return WorkflowService.isActionAllowedInWorkflow(action.service_slug, isNotification) && isAllowed;
	},
	[isInterfacePreviewEnabled.getter]: (state) => state.isInterfacePreviewEnabled,
	[editWorkflowHasInstallable.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[doesWorkflowHasInstallable.fullPath](state.selectedWorkflowId),
	[getSelectedWorkflowInstallable.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[getWorkflowInstallable.fullPath](state.selectedWorkflowId),
	[isPublishLoading.getter]: (state) => state.isPublishLoading,
	[getWorkflowAppearanceSettings.getter]: (state, getters) => {
		const wo = getters[editWorkflow.getter];
		return wo?.current_draft?.settings;
	},

	[customerAuthSettings.getter]: (state, getters) => {
		const { customer_auth_enabled, customer_auth_method, customer_auth_text } = getters[editWorkflow.getter]?.current_draft?.settings;
		return { customer_auth_enabled, customer_auth_method, customer_auth_text };
	},

	[isNarrowBranchesLayout.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[isNarrowBranchesLayoutFeatureFlagEnabled.fullPath] &&
		getters[editWorkflow.getter]?.user_settings?.is_narrow_branches_layout,
	[processWorkflowIds.getter]: (state, getters) => getters[editWorkflow.getter]?.process_workflows,
	[processWorkflows.getter]: (state, getters, rootState, rootGetters) =>
		(getters[processWorkflowIds.getter] || []).map(rootGetters[getWorkflow.fullPath]),

	// getters from `workflows` that are being "shadowed" here in `selectedWorkflow`
	[nodesData.getter]: (state, getters, rootState, rootGetters) => rootGetters[wfNodesData.fullPath](getters[selectedWorkflowId.getter]),
	[nodesDataArray.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfNodesDataArray.fullPath](getters[selectedWorkflowId.getter]),
	[nodesDataSorted.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfNodesDataSorted.fullPath](getters[selectedWorkflowId.getter]),
	[rootNode.getter]: (state, getters, rootState, rootGetters) => rootGetters[wfRootNode.fullPath](getters[selectedWorkflowId.getter]),
	[subflowNodes.getter]: (state, getters) => getters[nodesDataArray.getter].filter((node) => WorkflowNodeService.isSubflow(node)),
	[element.getter]: (state, getters, rootState, rootGetters) => rootGetters[wfElement.fullPath](getters[selectedWorkflowId.getter]),
	[uiElement.getter]: (state, getters, rootState, rootGetters) => rootGetters[wfUiElement.fullPath](getters[selectedWorkflowId.getter]),
	[nativeElement.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfNativeElement.fullPath](getters[selectedWorkflowId.getter]),
	[isNativeElement.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfIsNativeElement.fullPath](getters[selectedWorkflowId.getter]),
	[elementContextualData.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[wfElementContextualData.fullPath](getters[selectedWorkflowId.getter]),
	[sharedData.getter]: (state, getters, rootState, rootGetters) => rootGetters[workflowSharedData.fullPath](state.selectedWorkflowId),
	[isSharedWithOthers.getter]: (state, getters, rootState, rootGetters) =>
		rootGetters[isWorkflowSharedWithOthers.fullPath](state.selectedWorkflowId),
	[isRootNodeOfSubFlow.getter]: (state, getters, rootState, rootGetters) => (node) =>
		rootGetters[useIsRootNode.fullPath](node.id) && getters[isOfTypeSubFlow.getter],
};

export default getters;
